import { RainingGems } from "@/components/RainingGems/RainingGems";
import { appConfig } from "@/constants";
import type { WalletGems } from "@/domain/gems-game/data-access/lib/wallet_gems";
import type { LatestEarnings } from "@/domain/gems-game/domain";
import { Box, Stack } from "@biom3/react";
import Container from "@components/Container";
import { useEffect, useState } from "react";
import { GemsAnimation } from "./GemsAnimation";
import { GemsClaim } from "./GemsClaim";
import { GemsInfo } from "./GemsInfo";

interface VariableGemsProps {
  walletAddress: string | null;
  isPassportWallet: boolean;
  disconnect: () => Promise<void>;
  walletGems?: WalletGems;
  canClaim: boolean;
  hasJustEarned: boolean;
  onGemEarned: (walletGems: WalletGems) => void;
  eligibleGems: number;
  latestEarnings: LatestEarnings | undefined;
  onUTCDayChange: () => void;
}

const RAINING_GEMS_DURATION = 5000;
const RAINING_GEMS_DELAY = 1500;

export function VariableGems({
  walletAddress,
  walletGems,
  isPassportWallet,
  disconnect,
  canClaim,
  hasJustEarned,
  onGemEarned,
  eligibleGems,
  latestEarnings,
  onUTCDayChange,
}: VariableGemsProps) {
  const [itsRainingGems, setItsRainingGems] = useState(false);

  useEffect(() => {
    if (hasJustEarned) {
      setTimeout(() => {
        setItsRainingGems(true);
      }, RAINING_GEMS_DELAY);

      setTimeout(() => {
        setItsRainingGems(false);
      }, RAINING_GEMS_DURATION + RAINING_GEMS_DELAY);
    }
  }, [hasJustEarned]);

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "base.spacing.x2",
      }}
    >
      {itsRainingGems && <RainingGems />}
      <Box
        testId="claim-gems__background"
        sx={{
          position: "absolute",
          borderRadius: "base.spacing.x2",
          left: "0px",
          width: "100%",
          height: "100%",
          background: `url("${appConfig.ASSET_BASE_URL}/images/quests/noise.webp"), url("${appConfig.ASSET_BASE_URL}/images/quests/gradient.svg")`,
          backgroundSize: "auto, 100% 100%",
          backgroundBlendMode: "overlay, normal",
          backgroundRepeat: "repeat, no-repeat",
        }}
      />
      <Box
        testId="claim-gems"
        sx={{
          width: "100%",
          zIndex: 1,
          margin: "base.spacing.x4",
          position: "relative",
        }}
      >
        <Container
          sx={{
            paddingX: "base.spacing.x12",
          }}
        >
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={["base.spacing.x10", "base.spacing.x10", "0"]}
            alignItems="center"
            justifyContent={["center", "flex-start"]}
          >
            <Stack
              sx={{
                minWidth: ["auto", "auto", "auto", "350px"],
                height: "100%",
                paddingY: "base.spacing.x8",
                flex: {
                  default: "100%",
                  medium: "1",
                },
                alignItems: ["center", "flex-start"],
              }}
            >
              <GemsInfo
                hasJustEarned={hasJustEarned}
                canClaim={canClaim}
                walletAddress={walletAddress}
                walletGems={walletGems}
                eligibleGems={eligibleGems}
                latestEarnings={latestEarnings}
              />
            </Stack>
            <Stack
              direction={"row"}
              alignItems="center"
              justifyContent={["center", "flex-start"]}
              flexWrap="wrap"
            >
              <GemsAnimation
                eligibleGems={eligibleGems}
                hasJustEarned={hasJustEarned}
                canClaim={canClaim}
                walletAddress={walletAddress}
                latestEarnings={latestEarnings}
              />
              <GemsClaim
                walletAddress={walletAddress}
                walletGems={walletGems}
                canClaim={canClaim}
                hasJustEarned={hasJustEarned}
                onGemEarned={onGemEarned}
                latestEarnings={latestEarnings}
                onUTCDayChange={onUTCDayChange}
                disconnect={disconnect}
                isPassportWallet={isPassportWallet}
              />
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
